import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { useShopIdentity } from '../contexts/ShopIdentityContext';
import { getUserAvatarColor, getUserInitials, getUserDisplayName } from '../utils/avatarUtils';
import { billingApi, productionApi, registersApi } from '../utils/api';
import { ReactComponent as TwigsBranch } from '../assets/twigs-branch-icon.svg';
import { ReactComponent as TwigsLogotype } from '../assets/twigs-logotype.svg';
import PrintStationStatus from './PrintStationStatus';
import SidebarNavGroup, { forestNavItemClass, forestNavIconClass, ForestCountBadge, ForestRailBadge } from './SidebarNavGroup';
import {
  GridIcon, PlusIcon, ListIcon, TruckIcon, UsersIcon, CalendarIcon, SproutIcon,
  ClipboardIcon, ChartIcon, SettingsIcon, RouteIcon, FlaskIcon, HardHatIcon,
  RepeatIcon, BellIcon, ChevronLeftIcon, ChevronRightIcon, AlertCircleIcon, StoreIcon, BanknoteIcon, LogOutIcon,
  SparklesIcon,
} from './icons/LucideIcons';
import { useAppearance } from '../contexts/AppearanceContext';
import { staffNavLayout } from './sidebarNavUtils';
import { isOwner } from '../utils/roles';
import { useSettings } from '../hooks/useSettings';

// Forest collapses the sidebar to its 56px icon rail below this width
// (design-handoff/forest-theme §Interactions → Responsive).
const RAIL_QUERY = '(max-width: 1099px)';
const matchesRail = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(RAIL_QUERY).matches
    : false;

// Generate initials with SS rule (matching StaffMemberSelector)
const generateInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return '';

  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();

  // Special case: If both initials are S, use first 2 letters of first name
  if (firstInitial === 'S' && lastInitial === 'S') {
    return firstName.substring(0, 2).charAt(0).toUpperCase() +
           firstName.substring(1, 2).toLowerCase();
  }

  return firstInitial + lastInitial;
};

const Sidebar = () => {
  const { user, logout, canDrive } = useAuth();
  const navigate = useNavigate();
  // Forest re-skins the sidebar and restructures its footer; Fable renders the
  // markup it always has (the second return below).
  const { appearance } = useAppearance();
  const isForest = appearance !== 'fable';

  // Open problem reports, announced by IssueToasts (which already checks every
  // minute). While any are open, Problem reports sits under Dispatch.
  const [openIssues, setOpenIssues] = useState(0);
  useEffect(() => {
    const onCount = (e) => setOpenIssues(Number(e.detail) || 0);
    window.addEventListener('rosa:open-issues', onCount);
    return () => window.removeEventListener('rosa:open-issues', onCount);
  }, []);

  // Production order-ready badge count
  const [orderCount, setOrderCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    productionApi.getReadyCount().then((r) => { if (!cancelled && r.success) setOrderCount(r.data.count || 0); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Getting Started (tenant onboarding funnel): shown only while a
  // provisioned tenant still has open steps. Twigs (applicable=false) and
  // fully set-up tenants never see it. Non-admin staff get nothing (their
  // request 403s and the context reports no funnel). The same request
  // carries the shop's entitlements, which gate nav entries.
  const { onboarding, entitlements, known: entitlementsKnown } = useEntitlements();
  const shop = useShopIdentity();
  // Shops selling through Shopify or WooCommerce can hide the Online store
  // group (Settings → General); the pages stay reachable by link.
  const { settings: shopSettings } = useSettings();
  const onlineStoreInNav = shopSettings.onlineStoreInNav !== false;
  // The cash register: shops with cash_register_enabled on (not Twigs).
  const cashRegister = shopSettings.cashRegisterEnabled === true;
  // Owners: drawers closed over or short past the tolerance that nobody has
  // reviewed yet, as a count on Register (the Register screen lists them and
  // announces 'rosa:register-alerts' when one is marked reviewed).
  const [registerAlerts, setRegisterAlerts] = useState(0);
  const ownerAccount = isOwner(user);
  useEffect(() => {
    if (!cashRegister || !ownerAccount) { setRegisterAlerts(0); return undefined; }
    let cancelled = false;
    const load = () => registersApi.status()
      .then((r) => { if (!cancelled) setRegisterAlerts(((r && r.data && r.data.varianceAlerts) || []).length); })
      .catch(() => {});
    load();
    window.addEventListener('rosa:register-alerts', load);
    return () => { cancelled = true; window.removeEventListener('rosa:register-alerts', load); };
  }, [cashRegister, ownerAccount]);
  const showGettingStarted = !!(onboarding?.applicable && !onboarding.progress?.done);

  // Load collapsed state from localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebar_collapsed');
      return saved === 'true';
    } catch (error) {
      return false;
    }
  });

  // Forest only: below 1100px the sidebar is an icon rail regardless of the
  // saved choice. Expanding it there is temporary (not saved) and resets when
  // the window crosses the breakpoint, so the saved desktop choice survives.
  const [isNarrow, setIsNarrow] = useState(matchesRail);
  const [narrowExpanded, setNarrowExpanded] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(RAIL_QUERY);
    const onChange = (e) => {
      setIsNarrow(e.matches);
      setNarrowExpanded(false);
    };
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  const autoRail = isForest && isNarrow;
  const collapsed = autoRail ? !narrowExpanded : isCollapsed;
  // Right edge of the sidebar, for the fixed-position menus that open beside it.
  const sidebarRightEdge = isForest ? (collapsed ? 56 : 216) : (collapsed ? 64 : 208);

  // Staff member selection for staff accounts
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const isStaffAccount = user && user.accountType === 'staff';

  // Memoize staffMembers to prevent infinite loop
  const staffMembers = useMemo(() =>
    (user?.staffMembers || []).filter(m => m.active),
    [user?.staffMembers]
  );

  // Load selected staff member from localStorage
  useEffect(() => {
    if (!isStaffAccount) return;

    const loadSelectedStaffMember = () => {
      const saved = localStorage.getItem('selectedStaffMember');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const exists = staffMembers.find(m => m.id === parsed.id);
          if (exists) {
            setSelectedStaffMember(parsed);
          } else {
            setSelectedStaffMember(null);
          }
        } catch (e) {
          setSelectedStaffMember(null);
        }
      } else {
        setSelectedStaffMember(null);
      }
    };

    loadSelectedStaffMember();

    // Listen for storage events from StaffMemberSelector
    const handleStorageChange = () => {
      loadSelectedStaffMember();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isStaffAccount, staffMembers]);

  // Save collapsed state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('sidebar_collapsed', isCollapsed);
    } catch (error) {
      console.warn('Failed to save sidebar state:', error);
    }
  }, [isCollapsed]);

  // Check if user is ONLY a driver account (not admin/staff with driver capability)
  const isDriverOnly = user && user.accountType === 'driver';

  // Check if user has driver capability (for showing driver tab)
  const hasDriverCapability = user && (user.accountType === 'driver' || (canDrive && canDrive() && user.driverStatus === 'active'));

  // Get user avatar info - use selected staff member if staff account
  const rawAvatarColor = isStaffAccount && selectedStaffMember
    ? selectedStaffMember.avatarColor || '#10b981'
    : getUserAvatarColor(user);

  // Check if it's a hex color or Tailwind class
  const isHexColor = rawAvatarColor && rawAvatarColor.startsWith('#');
  const avatarColor = isHexColor ? rawAvatarColor : null;
  const avatarColorClass = !isHexColor ? rawAvatarColor : '';

  const initials = isStaffAccount && selectedStaffMember
    ? (selectedStaffMember.initials || generateInitials(selectedStaffMember.firstName, selectedStaffMember.lastName))
    : getUserInitials(user);
  const displayName = isStaffAccount && selectedStaffMember
    ? `${selectedStaffMember.firstName} ${selectedStaffMember.lastName}`
    : getUserDisplayName(user);

  // Billing notifications
  const [billingAlerts, setBillingAlerts] = useState([]);
  const [billingCount, setBillingCount] = useState(0);
  const [showBillingDropdown, setShowBillingDropdown] = useState(false);
  const [billingMenuCoords, setBillingMenuCoords] = useState({ top: 0, left: 0 });
  const billingButtonRef = useRef(null);
  const billingDropdownRef = useRef(null);

  const recomputeBillingMenuPosition = useCallback(() => {
    const btn = billingButtonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Position menu to the right of the sidebar, bottom-aligned with the button
    // (sidebarRightEdge matches w-16/w-52 in Fable, w-14/w-[216px] in Forest)
    setBillingMenuCoords({
      top: rect.bottom,
      left: sidebarRightEdge + 8, // 8px gap from sidebar edge
    });
  }, [sidebarRightEdge]);

  // Reposition when menu opens or sidebar collapse changes
  useEffect(() => {
    if (showBillingDropdown) {
      recomputeBillingMenuPosition();
    }
  }, [showBillingDropdown, recomputeBillingMenuPosition]);

  // Avatar dropdown (staff switcher + Profile)
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [avatarMenuCoords, setAvatarMenuCoords] = useState({ top: 0, left: 0 });
  const avatarButtonRef = useRef(null);
  const avatarMenuRef = useRef(null);

  const recomputeAvatarMenuPosition = useCallback(() => {
    const btn = avatarButtonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    // Position menu to the right of the sidebar, bottom-aligned with the button
    // (sidebarRightEdge matches w-16/w-52 in Fable, w-14/w-[216px] in Forest)
    setAvatarMenuCoords({
      top: rect.bottom,
      left: sidebarRightEdge + 8, // 8px gap from sidebar edge
    });
  }, [sidebarRightEdge]);

  // Reposition when menu opens or sidebar collapse changes
  useEffect(() => {
    if (showAvatarMenu) {
      recomputeAvatarMenuPosition();
    }
  }, [showAvatarMenu, recomputeAvatarMenuPosition]);

  // Close on outside click, Escape, or window resize
  useEffect(() => {
    if (!showAvatarMenu) return;

    const handleClickOutside = (e) => {
      if (
        avatarMenuRef.current?.contains(e.target) ||
        avatarButtonRef.current?.contains(e.target)
      ) return;
      setShowAvatarMenu(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowAvatarMenu(false);
    };
    const handleResize = () => recomputeAvatarMenuPosition();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [showAvatarMenu, recomputeAvatarMenuPosition]);

  const fetchBillingNotifications = useCallback(async () => {
    if (isDriverOnly) return;
    try {
      const response = await billingApi.getNotifications();
      if (response.success) {
        setBillingAlerts(response.data.accounts || []);
        setBillingCount(response.data.count || 0);
      }
    } catch (error) {
      // Silently fail — don't disrupt sidebar for notification errors
    }
  }, [isDriverOnly]);

  useEffect(() => {
    fetchBillingNotifications();
    const interval = setInterval(fetchBillingNotifications, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [fetchBillingNotifications]);

  // Close billing dropdown on outside click, Escape, or window resize
  useEffect(() => {
    if (!showBillingDropdown) return;

    const handleClickOutside = (e) => {
      if (
        billingDropdownRef.current?.contains(e.target) ||
        billingButtonRef.current?.contains(e.target)
      ) return;
      setShowBillingDropdown(false);
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowBillingDropdown(false);
    };
    const handleResize = () => recomputeBillingMenuPosition();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [showBillingDropdown, recomputeBillingMenuPosition]);

  const handleBillingNavigate = (customerId) => {
    setShowBillingDropdown(false);
    navigate(`/customers/${customerId}?tab=billing`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const toggleSidebar = () => {
    if (autoRail) setNarrowExpanded((v) => !v);
    else setIsCollapsed(!isCollapsed);
  };

  // Handle staff member switching
  const handleSelectStaffMember = (member) => {
    const memberWithInitials = {
      ...member,
      initials: member.initials || generateInitials(member.firstName, member.lastName)
    };
    setSelectedStaffMember(memberWithInitials);
    localStorage.setItem('selectedStaffMember', JSON.stringify(memberWithInitials));
    // Trigger storage event for other components
    window.dispatchEvent(new Event('storage'));
  };

  // Navigation items - different for driver-only accounts vs staff/admin
  const navItems = [];

  // Driver-only accounts: show only driver tab
  if (isDriverOnly) {
    navItems.push({
      path: '/driver',
      label: 'Driver',
      icon: <RouteIcon size={17} strokeWidth={2.25} />,
      color: 'blue'
    });
  } else {
    // Staff/Admin navigation
    navItems.push(
      {
        path: '/',
        label: 'Dashboard',
        icon: <GridIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/create-order',
        label: 'New order',
        icon: <PlusIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/orders',
        label: 'Orders',
        icon: <ListIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/register',
        label: 'Register',
        icon: <BanknoteIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/dispatch',
        label: 'Dispatch',
        icon: <TruckIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/issues',
        label: 'Issues',
        icon: <AlertCircleIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/customers',
        label: 'Customers',
        icon: <UsersIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/events',
        label: 'Events',
        icon: <CalendarIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/production',
        label: 'Production',
        icon: <SproutIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/install-board',
        label: 'Installs',
        icon: <HardHatIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/recurring-review',
        label: 'Recurring',
        icon: <RepeatIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/reports',
        label: 'Reports',
        icon: <ChartIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/formulas',
        label: 'Formulas',
        icon: <FlaskIcon size={17} strokeWidth={2.25} />
      },
      {
        path: '/proposals',
        label: 'Proposals',
        icon: <ClipboardIcon size={17} strokeWidth={2.25} />,
      }
    );

    // Add Driver tab for admin/staff with driver capability
    if (hasDriverCapability) {
      navItems.push({
        path: '/driver',
        label: 'Driver',
        icon: <RouteIcon size={17} strokeWidth={2.25} />,
        color: 'blue'
      });
    }
  }

  if (showGettingStarted && !isDriverOnly) {
    navItems.unshift({
      path: '/getting-started',
      label: 'Getting Started',
      icon: isForest ? <SparklesIcon size={17} strokeWidth={2.25} /> : (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" style={{ width: '20px', height: '20px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
        </svg>
      )
    });
  }

  // Resolve the desktop layout: flat items reuse their icon/label from navItems
  // (built above); groups reuse the icon of the item at their `iconPath`.
  if (!isDriverOnly) {
    navItems.push({
      path: '/store/products',
      label: 'Online store',
      icon: <StoreIcon size={17} strokeWidth={2.25} />,
    });
    navItems.push({
      path: '/settings',
      label: 'Settings',
      icon: <SettingsIcon size={17} strokeWidth={2.25} />,
    });
  }

  const itemByPath = Object.fromEntries(navItems.map((i) => [i.path, i]));
  const navEntries = isDriverOnly
    ? navItems.map((i) => ({ type: 'item', ...i }))
    : [...(showGettingStarted ? [{ type: 'item', path: '/getting-started' }] : []), ...staffNavLayout({ openIssues, entitlements, entitlementsKnown, onlineStoreInNav, owner: isOwner(user), cashRegister })].map((entry) => {
        if (entry.type === 'section') {
          return entry;
        }
        if (entry.type === 'group') {
          return {
            type: 'group',
            key: entry.key,
            label: entry.label,
            icon: itemByPath[entry.iconPath]?.icon,
            children: entry.children,
          };
        }
        // A layout item with no flat nav entry (Inbound orders on its own)
        // carries its own label and borrows the icon at its iconPath.
        const item = itemByPath[entry.path]
          || (entry.label ? { path: entry.path, label: entry.label, icon: itemByPath[entry.iconPath]?.icon } : null);
        if (!item) return null;
        const count = entry.path === '/issues' ? openIssues : entry.path === '/register' ? registerAlerts : 0;
        return { type: 'item', ...item, ...(count > 0 ? { count } : {}) };
      }).filter(Boolean);

  if (!user) {
    return null;
  }

  // Billing dropdown panel — fixed-position so it escapes the overflow-y-auto parent
  const billingDropdown = showBillingDropdown && (
    <div
      ref={billingDropdownRef}
      className="fixed z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-xl"
      style={{
        top: `${billingMenuCoords.top}px`,
        left: `${billingMenuCoords.left}px`,
        transform: 'translateY(-100%)', // bottom-align with bell button
      }}
      role="menu"
    >
      <div className="px-4 py-3 border-b border-gray-100">
        <h4 className="text-sm font-semibold text-gray-900">Billing Alerts</h4>
        <p className="text-xs text-gray-500 mt-0.5">
          {billingCount > 0 ? `${billingCount} account${billingCount !== 1 ? 's' : ''} ready to invoice` : 'All caught up'}
        </p>
      </div>
      {billingAlerts.length > 0 ? (
        <div className="max-h-64 overflow-y-auto">
          {billingAlerts.map((account) => (
            <button
              key={account.customerId}
              onClick={() => handleBillingNavigate(account.customerId)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-b-0 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900 text-sm">{account.customerName}</span>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="text-gray-400" style={{ width: '14px', height: '14px' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {account.unbilledCount} order{account.unbilledCount !== 1 ? 's' : ''} · {formatCurrency(account.unbilledTotal)}
              </div>
              <div className="text-xs text-amber-600 mt-0.5 capitalize">
                {account.billingCycle} billing
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="px-4 py-6 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="mx-auto text-gray-400 mb-2" style={{ width: '24px', height: '24px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <p className="text-sm text-gray-500">No billing alerts</p>
        </div>
      )}
    </div>
  );

  // Avatar dropdown — fixed-position so it escapes the overflow-y-auto parent
  const avatarMenu = showAvatarMenu && (
    <div
      ref={avatarMenuRef}
      className="fixed z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-xl"
      style={{
        top: `${avatarMenuCoords.top}px`,
        left: `${avatarMenuCoords.left}px`,
        transform: 'translateY(-100%)', // bottom-align with avatar button
      }}
      role="menu"
    >
      {/* Switch staff member section */}
      {isStaffAccount && staffMembers.length > 1 && (
        <>
          <div className="px-4 py-3 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Switch staff member
            </h4>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {staffMembers
              .filter((m) => m.id !== selectedStaffMember?.id)
              .map((member) => {
                const memberInitials =
                  member.initials || generateInitials(member.firstName, member.lastName);
                const memberColor = member.avatarColor || '#10b981';
                return (
                  <button
                    key={member.id}
                    onClick={() => {
                      handleSelectStaffMember(member);
                      setShowAvatarMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
                    role="menuitem"
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-xs flex-shrink-0"
                      style={{ backgroundColor: memberColor }}
                    >
                      {memberInitials}
                    </div>
                    <span className="text-sm text-gray-900 truncate">
                      {member.firstName} {member.lastName}
                    </span>
                  </button>
                );
              })}
          </div>
          <div className="border-t border-gray-100" />
        </>
      )}

      {/* Profile link */}
      <button
        onClick={() => {
          setShowAvatarMenu(false);
          navigate('/profile');
        }}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
        role="menuitem"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          style={{ width: '20px', height: '20px' }}
          className="text-gray-400 flex-shrink-0"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
        <span className="text-sm text-gray-900">Your Profile</span>
      </button>

      {/* Forest: Sign out lives here, as the menu's last item; the user row
          only opens this menu (its "· Sign out" is a label, not a control). */}
      {isForest && (
        <button
          onClick={() => {
            setShowAvatarMenu(false);
            logout();
          }}
          className="w-full flex items-center gap-3 px-4 py-3 border-t border-gray-100 hover:bg-gray-50 transition-colors text-left"
          role="menuitem"
        >
          <LogOutIcon size={18} className="text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-900">Sign out</span>
        </button>
      )}
    </div>
  );

  if (isForest) {
    const shopInitial = (shop?.name || '').trim().charAt(0).toUpperCase();
    // 32px initials in the staff member's own avatar color (same source as Fable).
    const forestAvatar = (
      <span
        aria-hidden="true"
        className={`w-8 h-8 flex-shrink-0 grid place-items-center rounded-full text-white text-xs font-bold ${avatarColorClass}`}
        style={avatarColor ? { backgroundColor: avatarColor } : undefined}
      >
        {initials}
      </span>
    );
    const billingTitle = billingCount > 0
      ? `${billingCount} account${billingCount !== 1 ? 's' : ''} need invoicing`
      : 'No billing alerts';

    return (
      <>
        {/* Sidebar - Hidden on mobile */}
        <aside
          className={`hidden md:flex flex-col flex-shrink-0 bg-white border-r border-gray-200 sticky top-0 h-screen supports-[height:100dvh]:h-dvh ${
            collapsed ? 'w-14' : 'w-[216px]'
          }`}
        >
          {/* Header: brand tile + shop name + collapse button */}
          <div
            className={`flex flex-shrink-0 pt-[18px] pb-2.5 ${
              collapsed ? 'flex-col items-center gap-2' : 'items-center justify-between pl-5 pr-4'
            }`}
          >
            <Link
              to="/"
              className="flex items-center gap-2.5 min-w-0 text-gray-900"
              aria-label={`${shop?.name || 'Home'} — go to Dashboard`}
            >
              {/* Twigs keeps its drawn branch + logotype, a shop with a logo shows
                  it, and only a shop with neither gets the initial tile. */}
              {shop?.isDefaultTenant ? (
                <span className="flex items-center gap-2.5 text-twigs" aria-hidden="true">
                  <TwigsBranch className="w-5 h-auto flex-shrink-0" />
                  {!collapsed && <TwigsLogotype className="flex-shrink-0" style={{ height: '19px' }} />}
                </span>
              ) : shop?.logoUrl ? (
                <img
                  src={shop.logoUrl}
                  alt=""
                  className={`object-contain ${collapsed ? 'h-7 w-8' : 'h-7 max-w-[140px]'}`}
                />
              ) : shop ? (
                <>
                  <span
                    aria-hidden="true"
                    className="w-[26px] h-[26px] flex-shrink-0 grid place-items-center rounded-[7px] bg-floral-700 text-white text-[13px] font-bold"
                  >
                    {shopInitial}
                  </span>
                  {!collapsed && (
                    <span aria-hidden="true" className="text-[15px] font-semibold tracking-[-0.01em] truncate">
                      {shop.name}
                    </span>
                  )}
                </>
              ) : null}
            </Link>
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="w-6 h-6 flex-shrink-0 grid place-items-center rounded-md text-gray-500 hover:bg-gray-50 transition-colors duration-[120ms]"
            >
              {collapsed ? <ChevronRightIcon size={14} /> : <ChevronLeftIcon size={14} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 min-h-0 overflow-y-auto flex flex-col gap-0.5 py-1 ${collapsed ? 'px-2' : 'px-3'}`}>
            {navEntries.map((entry) =>
              entry.type === 'section' ? (
                // Labels only; the icon rail keeps a gap where each one was.
                collapsed ? (
                  <div key={`section-${entry.label}`} className="h-3 flex-shrink-0" aria-hidden="true" />
                ) : (
                  <div
                    key={`section-${entry.label}`}
                    className="px-2 pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 select-none"
                  >
                    {entry.label}
                  </div>
                )
              ) : entry.type === 'group' ? (
                <SidebarNavGroup
                  key={entry.key}
                  group={entry}
                  isCollapsed={collapsed}
                  orderCount={orderCount}
                />
              ) : (
                <NavLink
                  key={entry.path}
                  to={entry.path}
                  end={entry.path === '/'}
                  className={({ isActive }) => forestNavItemClass({ active: isActive, collapsed, tone: entry.color })}
                  title={collapsed ? entry.label : ''}
                >
                  {({ isActive }) => (
                    <>
                      <span className={forestNavIconClass(isActive, entry.color)}>
                        {entry.icon}
                        {collapsed && entry.count > 0 && (
                          <ForestRailBadge count={entry.count} className="-top-2 -right-2.5" />
                        )}
                      </span>
                      {!collapsed && <span className="flex-1 whitespace-nowrap truncate">{entry.label}</span>}
                      {!collapsed && entry.count > 0 && <ForestCountBadge count={entry.count} />}
                    </>
                  )}
                </NavLink>
              )
            )}
          </nav>

          {/* Footer, above a hairline: billing alerts, printer status, user row */}
          <div className={`flex-shrink-0 border-t border-gray-200 flex flex-col gap-0.5 pt-3 pb-4 ${collapsed ? 'px-2' : 'px-3'}`}>
            {!isDriverOnly && (
              <button
                ref={billingButtonRef}
                type="button"
                onClick={() => setShowBillingDropdown((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showBillingDropdown}
                title={collapsed ? billingTitle : ''}
                className={`w-full flex items-center h-9 rounded-md text-[13px] font-medium transition-colors duration-[120ms] ${
                  collapsed ? 'justify-center' : 'gap-2.5 px-2.5'
                } ${billingCount > 0 ? 'text-warn-fg hover:bg-warn-bg' : 'text-gray-500 hover:bg-floral-50'}`}
              >
                <span className="relative flex-shrink-0 w-[18px] h-[18px] grid place-items-center">
                  <BellIcon size={17} strokeWidth={2.25} />
                  {collapsed && billingCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-danger ring-2 ring-white" aria-hidden="true" />
                  )}
                </span>
                {!collapsed && <span className="flex-1 text-left whitespace-nowrap truncate">Billing Alerts</span>}
                {!collapsed && billingCount > 0 && <ForestCountBadge count={billingCount} />}
              </button>
            )}

            {!isDriverOnly && <PrintStationStatus isCollapsed={collapsed} />}

            {/* User row: one button (avatar, name, "<Role> · Sign out" label)
                that opens the account menu. */}
            {collapsed ? (
              <button
                ref={avatarButtonRef}
                type="button"
                onClick={() => setShowAvatarMenu((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showAvatarMenu}
                aria-label="Account menu"
                title="Account menu"
                className="mt-1.5 flex items-center justify-center h-12 rounded-md hover:bg-floral-50 transition-colors duration-[120ms]"
              >
                {forestAvatar}
              </button>
            ) : (
              <button
                ref={avatarButtonRef}
                type="button"
                onClick={() => setShowAvatarMenu((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={showAvatarMenu}
                aria-label={`Account menu for ${displayName}`}
                className="mt-1.5 w-full flex items-center gap-2.5 h-12 px-1.5 rounded-md text-left hover:bg-floral-50 transition-colors duration-[120ms]"
              >
                {forestAvatar}
                <span className="flex-1 min-w-0 flex flex-col leading-[1.2]">
                  <span className="text-[13px] font-semibold text-gray-900 truncate">{displayName}</span>
                  {/* Plain text: Sign out itself is the last item in the account menu. */}
                  <span className="text-xs text-gray-500 truncate">
                    {user.role && <span className="capitalize">{user.role}</span>}
                    {user.role && ' · '}
                    Sign out
                  </span>
                </span>
                <ChevronRightIcon
                  size={14}
                  className={`flex-shrink-0 text-gray-500 transition-transform ${showAvatarMenu ? 'rotate-90' : ''}`}
                />
              </button>
            )}
          </div>
        </aside>

        {billingDropdown}
        {avatarMenu}
      </>
    );
  }

  return (
    <>
      {/* Sidebar - Hidden on mobile */}
      <aside
        className={`hidden md:flex flex-col bg-sidebar border-r-nav border-border shadow-sidebar transition-all duration-300 sticky top-0 h-screen supports-[height:100dvh]:h-dvh ${
          isCollapsed ? 'w-16' : 'w-52'
        }`}
      >
        {/* Brand lockup */}
        <div className={`flex items-center h-16 flex-shrink-0 overflow-hidden ${isCollapsed ? 'justify-center px-2' : 'pl-6 pr-4'}`}>
          <Link
            to="/"
            className={`flex items-center text-twigs transition-all duration-300 ${
              isCollapsed ? '' : 'gap-3'
            }`}
            aria-label={`${shop?.name || 'Home'} — go to Dashboard`}
          >
            {/* Twigs keeps its drawn lockup; other shops show their logo or name. */}
            {shop?.isDefaultTenant ? (
              <>
                <TwigsBranch className="w-5 h-auto flex-shrink-0" aria-hidden="true" />
                <TwigsLogotype
                  className={`flex-shrink-0 transition-all duration-300 ${
                    isCollapsed
                      ? 'opacity-0 -translate-x-2 max-w-0 pointer-events-none'
                      : 'opacity-100 translate-x-0 max-w-[200px]'
                  }`}
                  style={{ height: '21px' }}
                  aria-hidden="true"
                />
              </>
            ) : shop?.logoUrl ? (
              <img
                src={shop.logoUrl}
                alt=""
                className={`object-contain transition-all duration-300 ${isCollapsed ? 'h-7 w-8' : 'h-8 max-w-[160px]'}`}
              />
            ) : shop ? (
              <span className="font-heading text-lg font-semibold truncate max-w-[160px]" aria-hidden="true">
                {isCollapsed ? shop.name.charAt(0) : shop.name}
              </span>
            ) : null}
          </Link>
        </div>

        {/* Floating edge toggle */}
        <button
          onClick={toggleSidebar}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!isCollapsed}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="group absolute top-[22px] -right-2.5 z-[9999] w-5 h-5 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-500 hover:text-twigs transition-colors"
        >
          {/* Right-half border arc only — left side blends into the sidebar */}
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border border-gray-200 group-hover:border-floral-300 [clip-path:inset(0_0_0_50%)] pointer-events-none transition-colors"
          />
          <ChevronLeftIcon
            size={13}
            className={`transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Scroll column: nav + print status + billing + user section */}
        <div className="flex-1 flex flex-col overflow-y-auto">
        {/* Navigation Items */}
        <nav className="p-3 space-y-1">
          {navEntries.map((entry) =>
            entry.type === 'section' ? (
              // Labels only. Hidden in the icon rail, where there is no room for
              // them and the grouping reads from the gaps instead.
              isCollapsed ? null : (
                <div
                  key={`section-${entry.label}`}
                  className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500 select-none first:pt-1"
                >
                  {entry.label}
                </div>
              )
            ) : entry.type === 'group' ? (
              <SidebarNavGroup
                key={entry.key}
                group={entry}
                isCollapsed={isCollapsed}
                orderCount={orderCount}
              />
            ) : (
              <NavLink
                key={entry.path}
                to={entry.path}
                end={entry.path === '/'}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-floral-200 text-floral-900 font-bold [&_svg]:stroke-[2.5]'
                      : entry.color === 'blue'
                        ? 'text-floral-700 hover:bg-floral-50'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } ${isCollapsed ? 'justify-center' : ''}`
                }
                title={isCollapsed ? entry.label : ''}
              >
                <span className="relative flex-shrink-0">
                  {entry.icon}
                  {isCollapsed && entry.count > 0 && (
                    <span className="absolute -top-2 -right-2.5 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full">
                      {entry.count}
                    </span>
                  )}
                </span>
                {!isCollapsed && (
                  <span className="ml-3 whitespace-nowrap overflow-hidden">{entry.label}</span>
                )}
                {!isCollapsed && entry.count > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                    {entry.count}
                  </span>
                )}
              </NavLink>
            )
          )}
        </nav>

        {/* Print Station Status */}
        {!isDriverOnly && <PrintStationStatus isCollapsed={isCollapsed} />}

        {/* Billing Notifications Bell */}
        {!isDriverOnly && (
          <div className="px-3 pb-2 flex-shrink-0 relative">
            <button
              ref={billingButtonRef}
              onClick={() => setShowBillingDropdown((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={showBillingDropdown}
              className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                billingCount > 0 ? 'text-amber-700 hover:bg-amber-50' : 'text-gray-400 hover:bg-gray-50'
              } ${isCollapsed ? 'justify-center' : ''}`}
              title={isCollapsed ? (billingCount > 0 ? `${billingCount} account${billingCount !== 1 ? 's' : ''} need invoicing` : 'No billing alerts') : ''}
            >
              <span className="flex-shrink-0 relative">
                <BellIcon size={17} strokeWidth={2.25} />
                {billingCount > 0 && (
                  isCollapsed ? (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></span>
                  ) : (
                    <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                      {billingCount}
                    </span>
                  )
                )}
              </span>
              {!isCollapsed && (
                <span className="ml-3 text-sm font-medium whitespace-nowrap overflow-hidden">
                  Billing Alerts
                </span>
              )}
              {!isCollapsed && billingCount > 0 && (
                <span className="ml-auto px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full font-medium">
                  {billingCount}
                </span>
              )}
            </button>

            {billingDropdown}
          </div>
        )}

        {/* User Section — avatar with right-opening dropdown + Settings or Sign Out */}
        <div className="mt-auto p-3 space-y-1">
          {/* Inset rule: a border on the container itself runs edge to edge and
              collides with the sidebar's own right border. Inside the padding it
              stops short on both sides, as in the design. */}
          <div className="border-t border-border mb-2" aria-hidden="true" />

          {/* Avatar button */}
          <button
            ref={avatarButtonRef}
            onClick={() => setShowAvatarMenu((v) => !v)}
            className={`w-full flex items-center px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors ${
              isCollapsed ? 'justify-center' : ''
            }`}
            title={isCollapsed ? 'Account menu' : ''}
            aria-haspopup="menu"
            aria-expanded={showAvatarMenu}
          >
            <div
              className={`rounded-full flex items-center justify-center text-white font-medium flex-shrink-0 ${
                isCollapsed ? 'w-8 h-8 text-sm' : 'w-10 h-10'
              } ${avatarColorClass}`}
              style={avatarColor ? { backgroundColor: avatarColor } : {}}
            >
              {initials}
            </div>
            {!isCollapsed && (
              <>
                <div className="ml-3 overflow-hidden text-left flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {displayName}
                  </div>
                  <div className="text-xs text-gray-500 capitalize truncate">
                    {user.role}
                  </div>
                </div>
                <ChevronRightIcon
                  size={16}
                  className={`text-gray-400 transition-transform ${showAvatarMenu ? 'rotate-90' : ''}`}
                />
              </>
            )}
          </button>

          {/* Sign out, for every account (QA B P1-3: owners, admins and staff
              had no way out on desktop). Settings lives in the Back office
              nav group, where the design puts it. */}
          <button
            onClick={() => logout()}
            className={`w-full flex items-center px-3 py-2 rounded-lg text-red-700 hover:bg-red-50 transition-colors ${
              isCollapsed ? 'justify-center' : ''
            }`}
            title={isCollapsed ? 'Sign out' : ''}
          >
            <LogOutIcon size={20} className="flex-shrink-0 text-red-400" />
            {!isCollapsed && (
              <span className="ml-3 text-sm whitespace-nowrap overflow-hidden">
                Sign out
              </span>
            )}
          </button>
        </div>
        </div>
      </aside>

      {avatarMenu}
    </>
  );
};

export default Sidebar;
